// Core packages
import { registerLocaleData } from "@angular/common";
import localeEn from "@angular/common/locales/en";
import localeIt from "@angular/common/locales/it";
import { LOCALE_ID, NgModule } from "@angular/core";
import { ReactiveFormsModule } from "@angular/forms";
import { GoogleMapsModule } from "@angular/google-maps";
import { BrowserModule } from "@angular/platform-browser";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";

// Third party packages
import { FormlyModule } from "@ngx-formly/core";
import { FormlyMaterialModule } from "@ngx-formly/material";
import { TranslateModule } from "@ngx-translate/core";
import { ChartsModule } from "ng2-charts";
import { CookieService } from "ngx-cookie-service";

// Custom packages - Components
import { AppComponent } from "src/app/app.component";
import { AdminQRCodeMappingComponent } from "src/app/components/adminQRCodeMapping/adminQRCodeMapping.component";
import { HomeComponent } from "src/app/components/home/home.component";
import { PrivacyDocsPublicAccessComponent } from "src/app/components/privacyDocsPublicAccess/privacyDocsPublicAccess.component";
import { PublicQRCodeComponent } from "src/app/components/publicQRCode/publicQRCode.component";
import { SupplierSecurityVideoComponent } from "src/app/components/supplierSecurityVideo/supplierSecurityVideo.component";

// Custom packages - Modules
import { AuthModule } from "src/app/modules/auth/auth.module";
import { BalancesModule } from "src/app/modules/balances/balances.module";
import { DeadlinesModule } from "src/app/modules/deadlines/deadlines.module";
import { ErrorsModule } from "src/app/modules/errors/errors.module";
import { GroundingsModule } from "src/app/modules/groundings/groundings.module";
import { PublicModule } from "src/app/modules/public/public.module";
import { AppRoutingModule } from "./app-routing.module";
import { AccessLogsModule } from "./modules/access-logs/access-logs.module";
import { AddDocumentsModule } from "./modules/add-documents/add-documents.module";
import { CaretakersModule } from "./modules/caretakers/caretakers.module";
import { CommunicationsModule } from "./modules/communications/communications.module";
import { CompaniesModule } from "./modules/companies/companies.module";
import { DdtsModule } from "./modules/ddts/ddts.module";
import { ElectricMetersModule } from "./modules/electric-meters/electric-meters.module";
import { GasMetersModule } from "./modules/gas-meters/gas-meters.module";
import { PersonalDataModule } from "./modules/personal-data/personal-data.module";
import { PropertiesModule } from "./modules/properties/properties.module";
import { RolesModule } from "./modules/roles/roles.module";
import { SearchDocsModule } from "./modules/search-docs/search-docs.module";
import { SharedModule } from "./modules/shared.module";
import { SummaryDocsModule } from "./modules/summary-docs/summary-docs.module";
import { UsersModule } from "./modules/users/users.module";
import { WaterMetersModule } from "./modules/water-meters/water-meters.module";

// Custom packages - Miscellaneous
import { DisclaimerDialogComponent } from 'src/app/components/disclaimer-dialog/disclaimer-dialog.component';
import { PrivacyDialogComponent } from "src/app/components/privacy-dialog/privacy-dialog.component";
import { AddSupplierDialogComponent } from 'src/app/modules/suppliers/components/add-supplier-dialog/add-supplier-dialog.component';
import { AppWrapperComponent } from "./components/app-wrapper/app-wrapper.component";
import { GenericDocsModule } from './modules/generic-docs/generic-docs.module';
import { HistoricalDocsModule } from './modules/historical-docs/historical-docs.module';
import { InterventionsModule } from './modules/interventions/interventions.module';
import { SettingsModule } from './modules/settings/settings.module';
import { StatsModule } from './modules/stats/stats.module';
import { TankModule } from './modules/tank/tank.module';
import { CategoriesDialogComponent } from "./shared/components/dialogs/categories-dialog/categories-dialog.component";
import { ChangePasswordDialogComponent } from "./shared/components/dialogs/change-password-dialog/change-password-dialog.component";
import { ChooseRecipientDialogComponent } from './shared/components/dialogs/choose-recipient-dialog/choose-recipient-dialog.component';
import { ConfirmDialogComponent } from "./shared/components/dialogs/confirm-dialog/confirm-dialog.component";
import { ViewNotesDialogComponent } from './shared/components/dialogs/document-note-dialog/view-notes-dialog.component';
import { InfoDialogComponent } from "./shared/components/dialogs/info-dialog/info-dialog.component";
import { LightboxDialogComponent } from "./shared/components/dialogs/lightbox-dialog/lightbox-dialog.component";
import { ManualAddressDialogComponent } from "./shared/components/dialogs/manual-address-dialog/manual-address-dialog.component";
import { PrintQrDialogComponent } from './shared/components/dialogs/print-qr-dialog/print-qr-dialog.component';
import { SITISRLDialogComponent } from "./shared/components/dialogs/sitisrl-dialog/sitisrl-dialog.component";
import { UploadCompanyDocDialogComponent } from './shared/components/dialogs/upload-company-doc-dialog/upload-company-doc-dialog.component';
import { UploadFileDialogComponent } from './shared/components/dialogs/upload-file-dialog/upload-file-dialog.component';
import { VideoDialogComponent } from './shared/components/dialogs/video-dialog/video-dialog.component';
import { FooterComponent } from "./shared/components/footer/footer.component";
import { addonsExtension } from "./shared/components/formly/addons.extensions";
import { FormlyWrapperAddons } from "./shared/components/formly/addons.wrapper";
import { AddressTypeComponent } from './shared/components/formly/address-type/address-type.component';
import { AttachmentsTypeComponent } from './shared/components/formly/attachments-type/attachments-type.component';
import { AutocompleteSupplierTypeComponent } from "./shared/components/formly/autocomplete-supplier-type/autocomplete-supplier-type.component";
import { AutocompleteTypeComponent } from "./shared/components/formly/autocomplete-type/autocomplete-type.component";
import { ButtonTypeComponent } from './shared/components/formly/button-type/button-type.component';
import { DayMonthPickerComponent, DayMonthPickerHeaderComponent } from "./shared/components/formly/day-month-picker-type/day-month-picker-type";
import { FileDownloaderTypeComponent } from './shared/components/formly/file-downloader-type/file-downloader-type.component';
import { FileUploaderStructureTypeComponent } from "./shared/components/formly/file-uploader-structure-type/file-uploader-structure-type.component";
import { FileUploaderTypeComponent } from "./shared/components/formly/file-uploader-type/file-uploader-type.component";
import { GoogleAutocompleteTypeComponent } from "./shared/components/formly/google-autocomplete-type/google-autocomplete-type.component";
import { HorizontalWrapperComponent } from './shared/components/formly/horizontal-wrapper-type/horizontal-wrapper-type.component';
import { QrcodeStructureTypeComponent } from "./shared/components/formly/qrcode-structure-type/qrcode-structure-type.component";
import { RepeatDocTypeComponent } from "./shared/components/formly/repeat-doc-type/repeat-doc-type.component";
import { RepeatScaleTypeComponent } from "./shared/components/formly/repeat-scale-type/repeat-scale-type.component";
import { RepeatStructureoptionsTypeComponent } from './shared/components/formly/repeat-structure-options-type/repeat-structure-options-type.component';
import { RepeatTypeComponent } from "./shared/components/formly/repeat-type/repeat-type.component";
import { SelectElectricMeterTypeComponent } from "./shared/components/formly/select-electric-meter-type/select-electric-meter-type.component";
import { SelectGasMeterTypeComponent } from "./shared/components/formly/select-gas-meter-type/select-gas-meter-type.component";
import { SelectStructureTypeComponent } from "./shared/components/formly/select-structure-type/select-structure-type.component";
import { SelectWaterMeterTypeComponent } from "./shared/components/formly/select-water-meter-type/select-water-meter-type.component";
import { SingleFileViewerTypeComponent } from './shared/components/formly/single-file-viewer-type/single-file-viewer-type.component';
import { SlideToggleTypeComponent } from './shared/components/formly/slide-toggle-type/slide-toggle-type.component';
import { HeaderComponent } from "./shared/components/header/header.component";
import { SidenavComponent } from "./shared/components/sidenav/sidenav.component";

// Register all available locales
registerLocaleData(localeIt, "it");
registerLocaleData(localeEn, "en");

// Set date parse and format standard
export const CUSTOM_FORMATS = {
  parse: {
    dateInput: "DD/MM/YYYY",
  },
  display: {
    dateInput: "DD/MM/YYYY",
    monthYearLabel: "MM YYYY",
    dateA11yLabel: "DD/MM/YYYY",
    monthYearA11yLabel: "MM YYYY",
  },
};

@NgModule({
  imports: [
    // Core modules
    BrowserModule,
    BrowserAnimationsModule,
    ReactiveFormsModule,
    GoogleMapsModule,

    // Shared modules
    SharedModule.forRoot(),

    // Routing
    AppRoutingModule,

    // Third party modules
    TranslateModule.forRoot(),
    FormlyMaterialModule,
    FormlyModule.forRoot({
      types: [
        {
          name: "autocomplete",
          component: AutocompleteTypeComponent,
          // wrappers: ['form-field'],
        },
        {
          name: "autocomplete-supplier",
          component: AutocompleteSupplierTypeComponent,
        },
        {
          name: "google-autocomplete",
          component: GoogleAutocompleteTypeComponent,
          wrappers: ["form-field"],
        },
        {
          name: "file-uploader",
          component: FileUploaderTypeComponent,
        },
        {
          name: "file-downloader",
          component: FileDownloaderTypeComponent,
        },
        {
          name: "single-file-viewer",
          component: SingleFileViewerTypeComponent,
        },
        {
          name: "file-uploader-structure",
          component: FileUploaderStructureTypeComponent,
        },
        {
          name: "repeat",
          component: RepeatTypeComponent,
          wrappers: ["form-field"],
        },
        {
          name: "select-structure",
          component: SelectStructureTypeComponent,
          wrappers: ["form-field"],
        },
        {
          name: "select-electric-meter",
          component: SelectElectricMeterTypeComponent,
          wrappers: ["form-field"],
        },
        {
          name: "select-gas-meter",
          component: SelectGasMeterTypeComponent,
          wrappers: ["form-field"],
        },
        {
          name: "select-water-meter",
          component: SelectWaterMeterTypeComponent,
          wrappers: ["form-field"],
        },
        {
          name: "attachments",
          component: AttachmentsTypeComponent,
          wrappers: ["form-field"],
        },
        {
          name: "qr-code-structure",
          component: QrcodeStructureTypeComponent,
          wrappers: ["form-field"],
        },
        {
          name: "repeat-scale",
          component: RepeatScaleTypeComponent,
          wrappers: ["form-field"],
        },
        {
          name: "slide-toggle",
          component: SlideToggleTypeComponent,
          wrappers: ["form-field"],
        },
        {
          name: "button",
          component: ButtonTypeComponent,
          wrappers: ["form-field"],
          defaultOptions: {
            templateOptions: {
              btnType: "default",
              type: "button",
            },
          },
        },
        {
          name: "repeat-doc",
          component: RepeatDocTypeComponent,
          wrappers: ["form-field"],
        },
        {
          name: "repeat-structure-options",
          component: RepeatStructureoptionsTypeComponent,
          wrappers: ["form-field"],
        },
        {
          name: "address",
          component: AddressTypeComponent,
          wrappers: ["form-field"],
        },
        {
          name: "day-month-picker",
          component: DayMonthPickerComponent,
          wrappers: ["form-field"],
        },
      ],
      wrappers: [
        { name: "addons", component: FormlyWrapperAddons },
        {
          name: "form-field-horizontal",
          component: HorizontalWrapperComponent,
        },
      ],
      extensions: [
        { name: "addons", extension: { onPopulate: addonsExtension } },
      ],
      validationMessages: [
        { name: "required", message: "This field is required" },
      ],
    }),
    ChartsModule,

    // Custom modules
    AuthModule,
    ErrorsModule,
    PublicModule,
    UsersModule,
    RolesModule,
    CompaniesModule,
    PropertiesModule,
    CommunicationsModule,
    AccessLogsModule,
    BalancesModule,
    DeadlinesModule,
    GroundingsModule,
    CaretakersModule,
    SearchDocsModule,
    SummaryDocsModule,
    AddDocumentsModule,
    DdtsModule,
    StatsModule,
    SettingsModule,
    InterventionsModule,
    TankModule,
    ElectricMetersModule,
    GasMetersModule,
    WaterMetersModule,
    HistoricalDocsModule,
    PersonalDataModule,
    GenericDocsModule,
  ],
  declarations: [
    AppComponent,

    // Components
    HomeComponent,
    PublicQRCodeComponent,
    AutocompleteTypeComponent,
    AutocompleteSupplierTypeComponent,
    GoogleAutocompleteTypeComponent,
    FileUploaderTypeComponent,
    RepeatTypeComponent,
    SelectStructureTypeComponent,
    SelectElectricMeterTypeComponent,
    SelectGasMeterTypeComponent,
    SelectWaterMeterTypeComponent,
    AttachmentsTypeComponent,
    HorizontalWrapperComponent,
    FileDownloaderTypeComponent,
    SingleFileViewerTypeComponent,
    SlideToggleTypeComponent,
    ButtonTypeComponent,
    DayMonthPickerComponent,
    DayMonthPickerHeaderComponent,
    SupplierSecurityVideoComponent,
    AdminQRCodeMappingComponent,
    PrivacyDocsPublicAccessComponent,

    AppWrapperComponent,
    HeaderComponent,
    FooterComponent,
    SidenavComponent,
    ConfirmDialogComponent,
    LightboxDialogComponent,
    ManualAddressDialogComponent,
    SITISRLDialogComponent,
    ChangePasswordDialogComponent,
    FileUploaderStructureTypeComponent,
    QrcodeStructureTypeComponent,
    RepeatScaleTypeComponent,
    UploadFileDialogComponent,
    UploadCompanyDocDialogComponent,
    ChooseRecipientDialogComponent,
    PrintQrDialogComponent,
    ViewNotesDialogComponent,
    InfoDialogComponent,
    DisclaimerDialogComponent,
    PrivacyDialogComponent,

    RepeatDocTypeComponent,
    RepeatStructureoptionsTypeComponent,

    AddressTypeComponent,
    CategoriesDialogComponent,
    VideoDialogComponent,
    AddSupplierDialogComponent,

    // Misc
    FormlyWrapperAddons,
  ],
  providers: [
    {
      provide: LOCALE_ID,
      useValue: "it",
    },
    CookieService,
  ],
  entryComponents: [
    // Only components declared in AppModule's declarations
    ConfirmDialogComponent,
    LightboxDialogComponent,
    ManualAddressDialogComponent,
    SITISRLDialogComponent,
    ChangePasswordDialogComponent,
    UploadFileDialogComponent,
    UploadCompanyDocDialogComponent,
    ChooseRecipientDialogComponent,
    PrintQrDialogComponent,
    ViewNotesDialogComponent,
    InfoDialogComponent,
    DisclaimerDialogComponent,
    PrivacyDialogComponent,
    DayMonthPickerHeaderComponent,
    CategoriesDialogComponent,
    VideoDialogComponent,
    AddSupplierDialogComponent,
  ],
  bootstrap: [AppComponent],
  exports: [
    // Components
    AppWrapperComponent,
    HomeComponent,
    PublicQRCodeComponent,
    HeaderComponent,
    FooterComponent,
    SidenavComponent,
    SupplierSecurityVideoComponent,
    AdminQRCodeMappingComponent,
    PrivacyDocsPublicAccessComponent,
  ],
})
export class AppModule {}
